import { http, HttpResponse } from "msw";

import { AUTH_CODES } from "@/lib/api/session";
import {
  db,
  findUserByCode,
  isLocked,
  loggedInUser,
  LOCKOUT_DURATION_MS,
  LOCKOUT_THRESHOLD,
  toLoginSessionUser,
  toSessionUser,
} from "../db";
import { BRANCHES } from "../fixtures/branches";

const PROBLEM_TYPE_BASE = "https://erp.example/problems/";

/** Email that always answers 429, for exercising rate-limit UX. */
export const RATE_LIMITED_CODE = "9429";

/** Reset token that always answers 422 auth.invalid_token. */
export const EXPIRED_RESET_TOKEN = "expired";

function problemResponse(
  status: number,
  code: string,
  title: string,
  extra?: Record<string, unknown>,
) {
  return HttpResponse.json(
    { type: PROBLEM_TYPE_BASE + code, title, status, code, ...extra },
    { status, headers: { "Content-Type": "application/problem+json" } },
  );
}

function lockedResponse() {
  const lockedUntil = (
    db.auth.lockedUntil ?? new Date(Date.now() + LOCKOUT_DURATION_MS)
  ).toISOString();
  return problemResponse(423, AUTH_CODES.accountLocked, "Account locked", {
    detail: `Account locked until ${lockedUntil}`,
    locked_until: lockedUntil,
  });
}

function isWeakPassword(password: string): boolean {
  return password.length < 12 || !/[0-9]/.test(password) || !/[a-zA-Z]/.test(password);
}

interface LoginBody {
  code?: string;
  password?: string;
}

interface ResetCompleteBody {
  token?: string;
  password?: string;
}

export const sessionHandlers = [
  http.post("/api/v1/session", async ({ request }) => {
    const body = (await request.json().catch(() => ({}))) as LoginBody;
    const code = body.code ?? "";
    const password = body.password ?? "";

    if (code.toLowerCase() === RATE_LIMITED_CODE) {
      return problemResponse(429, AUTH_CODES.rateLimited, "Too many requests", {
        detail: "Rate limit exceeded. Try again later.",
      });
    }

    if (isLocked()) {
      return lockedResponse();
    }
    if (db.auth.lockedUntil !== null) {
      // Lock expired — start a fresh window.
      db.auth.lockedUntil = null;
      db.auth.failedAttempts = 0;
    }

    const user = findUserByCode(code);
    if (!user || user.password !== password) {
      db.auth.failedAttempts += 1;
      if (db.auth.failedAttempts >= LOCKOUT_THRESHOLD) {
        db.auth.lockedUntil = new Date(Date.now() + LOCKOUT_DURATION_MS);
        return lockedResponse();
      }
      return problemResponse(401, AUTH_CODES.invalidCredentials, "Invalid credentials");
    }

    db.auth.failedAttempts = 0;
    db.auth.lockedUntil = null;
    db.auth.loggedInUserId = user.id;
    db.auth.cookieSessionValid = true;
    return HttpResponse.json(toLoginSessionUser(user), {
      status: 200,
      headers: { "Set-Cookie": "erp_session=mock; Path=/; SameSite=Lax" },
    });
  }),

  http.get("/api/v1/session", ({ cookies, request }) => {
    // Mirror real cookie-session semantics: the in-memory login flag dies on a
    // full page load, but the browser still carries the session cookie — treat
    // a valid cookie as the session, exactly like the scs store will.
    if (
      db.auth.loggedInUserId === null &&
      db.auth.cookieSessionValid &&
      cookies["erp_session"] === "mock"
    ) {
      db.auth.loggedInUserId = db.users[0]?.id ?? null;
    }
    const user = loggedInUser();
    if (!user) {
      return problemResponse(401, AUTH_CODES.unauthenticated, "Not authenticated");
    }

    const rawActingBranch = request.headers.get("X-Acting-Branch-Id");
    if (rawActingBranch !== null && rawActingBranch.includes(",")) {
      return problemResponse(400, "branch_header_invalid", "Invalid acting branch header");
    }
    if (
      rawActingBranch !== null &&
      !/^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(rawActingBranch)
    ) {
      return problemResponse(400, "branch_header_invalid", "Invalid acting branch header");
    }

    const activeAllowed = user.allowed_branch_ids.filter((branchId) =>
      BRANCHES.some((branch) => branch.id === branchId && branch.status === "active"),
    );
    if (rawActingBranch !== null && !activeAllowed.includes(rawActingBranch)) {
      return problemResponse(403, "branch_not_allowed", "Acting branch is not allowed");
    }
    const defaultBranch =
      user.default_branch_id !== null && activeAllowed.includes(user.default_branch_id)
        ? user.default_branch_id
        : null;
    return HttpResponse.json(
      toSessionUser(user, rawActingBranch === null ? defaultBranch : rawActingBranch),
    );
  }),

  http.delete("/api/v1/session", () => {
    db.auth.loggedInUserId = null;
    db.auth.cookieSessionValid = false;
    return new HttpResponse(null, {
      status: 204,
      headers: { "Set-Cookie": "erp_session=; Path=/; Max-Age=0" },
    });
  }),

  // Anti-enumeration: identical 202 whether or not the account exists.
  http.post("/api/v1/password-resets", async ({ request }) => {
    await request.json().catch(() => null);
    return new HttpResponse(null, { status: 202 });
  }),

  http.post("/api/v1/password-resets/complete", async ({ request }) => {
    const body = (await request.json().catch(() => ({}))) as ResetCompleteBody;
    const token = body.token ?? "";
    const password = body.password ?? "";

    if (token === "" || token === EXPIRED_RESET_TOKEN) {
      return problemResponse(422, AUTH_CODES.invalidToken, "Invalid or expired reset token");
    }
    if (isWeakPassword(password)) {
      return problemResponse(422, AUTH_CODES.weakPassword, "Password does not meet requirements", {
        errors: [
          {
            field: "password",
            code: AUTH_CODES.weakPassword,
            message: "Password must be at least 12 characters and include letters and digits.",
          },
        ],
      });
    }
    return new HttpResponse(null, { status: 204 });
  }),
];
